import React, { type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { BorderBeam } from "@/components/ui/border-beam";
import { cn } from "@/lib/utils";
import { panelEyebrow, panelEmpty, panelPill } from "@/lib/panel-ui";

export function PanelStat({
  label,
  value,
  hero,
}: {
  label: string;
  value: string | number;
  hero?: boolean;
}) {
  return (
    <div className="relative h-full overflow-hidden rounded-[28px] border border-border/60 bg-card">
      {hero && <BorderBeam size={80} duration={10} />}
      <div className="relative min-w-0 p-4 sm:p-6">
        <p className={panelEyebrow}>{label}</p>
        <p
          className={cn(
            "mt-2 break-words font-display font-semibold tabular-nums tracking-tight",
            hero ? "text-2xl sm:text-4xl" : "text-2xl sm:text-3xl",
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

export const PanelIconBtn = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    children: ReactNode;
    className?: string;
  }
>(({ label, onClick, disabled, children, className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-[color,transform] duration-150 ease-out hover:text-foreground active:scale-95 disabled:opacity-40",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
PanelIconBtn.displayName = "PanelIconBtn";

export function PanelEmpty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className={cn(panelEmpty, "flex flex-col items-center justify-center px-6 py-14 text-center")}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function PanelPagination({
  page,
  totalPages,
  info,
  onPrev,
  onNext,
  prevLabel,
  nextLabel,
  disabled,
}: {
  page: number;
  totalPages: number;
  info: string;
  onPrev: () => void;
  onNext: () => void;
  prevLabel: string;
  nextLabel: string;
  disabled?: boolean;
}) {
  if (totalPages <= 1 && !info) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-6">
      <p className="text-xs text-muted-foreground">{info}</p>
      {totalPages > 1 && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onPrev} disabled={disabled || page <= 1} className={panelPill}>
            {prevLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={disabled || page >= totalPages}
            className={panelPill}
          >
            {nextLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export const panelTableHead =
  "px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap";

export const panelTableCell = "px-4 py-3.5 whitespace-nowrap text-sm";
